/* --------------------------------------------------------------------------
 * src/config/config.js
 * Merkezî API konfigürasyonu – "tak-çıkar" format
 * -------------------------------------------------------------------------- */

class APIConfig {
  /* ---------------------------------------------------- 1) Temel ayarlar */
  DEVELOPMENT_MODE = process.env.NODE_ENV === 'development'; // false → production
  API_SETTINGS = {
    HOST: process.env.REACT_APP_BACKEND_HOST || 'localhost', // backend IP - .env'den alınır
    PORT: process.env.REACT_APP_BACKEND_PORT || '9072', // backend PORT - .env'den alınır
    PROTOCOL: process.env.NODE_ENV === 'production' ? 'https' : 'http',
    BASE_PATH: process.env.REACT_APP_API_BASE_PATH || '/api',

    FRONTEND_HOST: process.env.REACT_APP_FRONTEND_HOST || 'localhost',
    FRONTEND_PORT: process.env.REACT_APP_FRONTEND_PORT || '9071',
  };

  ENDPOINTS = {
    AUTH: {
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      REFRESH: '/auth/refresh',
      VERIFY: '/auth/verify',
    },
    USER: { PROFILE: '/user/profile', UPDATE: '/user/update' },
    DATA: {
      LIST: '/data',
      CREATE: '/data/create',
      UPDATE: '/data/update',
      DELETE: '/data/delete',
    },
  };

  REQUEST_CONFIG = {
    TIMEOUT: 50_000,
    RETRIES: 3,
    RETRY_DELAY: 1_000,
    HEADERS: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };

  //  HttpClient ile uyumlu yardımcı alanlar
  MAX_RETRIES = (process.env.NODE_ENV === 'production') ? 3 : 0;
  RETRY_DELAY = 1_000;
  REQUEST_THROTTLE_MS = 100;
  rateLimitMap = new Map();

  /* ------------------------------------------------- 2) Kurucu & yardımcılar */
  constructor() {
    this.BASE_URL = this.#buildBaseURL();
    this.FRONTEND_URL = this.#buildFrontendURL();
    this.#log();
  }

  // Ortam yardımcıları
  get isDevelopment() {
    return this.DEVELOPMENT_MODE;
  }
  get isProduction() {
    return !this.DEVELOPMENT_MODE;
  }
  get TIMEOUT() {
    return this.REQUEST_CONFIG.TIMEOUT;
  }

  #buildBaseURL() {
    // http(s)://host:port/api
    const direct = process.env.REACT_APP_BACKEND_URL;
    if (direct && /^https?:\/\//.test(direct)) {
      return `${direct.replace(
        /\/?$/,
        ''
      )}${this.API_SETTINGS.BASE_PATH.replace(/\/?$/, '')}`;
    }
    const { PROTOCOL, HOST, PORT, BASE_PATH } = this.API_SETTINGS;
    return `${PROTOCOL}://${HOST}:${PORT}${BASE_PATH.replace(/\/?$/, '')}`;
  }
  #buildFrontendURL() {
    const direct = process.env.REACT_APP_FRONTEND_URL;
    if (direct && /^https?:\/\//.test(direct)) {
      return direct.replace(/\/+$/, '');
    }
    const { FRONTEND_HOST, FRONTEND_PORT } = this.API_SETTINGS;
    const protocol = process.env.NODE_ENV === 'production' ? 'https' : 'http';
    return `${protocol}://${FRONTEND_HOST}:${FRONTEND_PORT}`;
  }

  /* ---------------------------------------- 3) Runtime konfigürasyon güncelle */
  update(obj = {}) {
    Object.assign(this.API_SETTINGS, obj);
    this.BASE_URL = this.#buildBaseURL();
    this.FRONTEND_URL = this.#buildFrontendURL();
    this.#log();
  }

  /* --------------------------------------------- 4) Endpoint → tam URL üretimi */
  getURL(endpoint) {
    if (typeof endpoint === 'object') endpoint = Object.values(endpoint)[0];
    endpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${this.BASE_URL}${endpoint}`;
  }

  /* --------------------------------------- 5) localhost ↔ IP dönüştürme yardımcısı */
  #deLocal(url) {
    const { HOST, PORT } = this.API_SETTINGS;
    return url
      .replace(/http:\/\/localhost(?::\d+)?/g, `http://${HOST}:${PORT}`)
      .replace(/http:\/\/127\.0\.0\.1(?::\d+)?/g, `http://${HOST}:${PORT}`);
  }

  /* ------------------------------------------------------ 6) Fetch sarmalayıcı */
  async fetch(input, options = {}) {
    /* --- URL muhafazası ----------------------------------------------- */
    let url = input;
    if (url.startsWith('/'))
      // sadece "/path" geldiyse backend'e bağla
      url = `${this.BASE_URL}${url}`;
    url = this.#deLocal(url); // kalan localhostları temizle

    /* --- Opsiyonlar ---------------------------------------------------- */
    const opts = {
      method: 'GET',
      ...options,
      headers: { ...this.REQUEST_CONFIG.HEADERS, ...options.headers },
    };

    /* --- Tekrarlı istek mantığı --------------------------------------- */
    const { RETRIES, RETRY_DELAY, TIMEOUT } = this.REQUEST_CONFIG;
    for (let attempt = 0; attempt <= RETRIES; attempt++) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), TIMEOUT);
        const res = await fetch(url, { ...opts, signal: controller.signal });
        clearTimeout(timer);
        if (!res.ok && attempt < RETRIES) throw new Error(`HTTP ${res.status}`);
        return res;
      } catch (err) {
        if (attempt >= RETRIES) throw err;
        await new Promise((r) => setTimeout(r, RETRY_DELAY));
      }
    }
  }

  // HttpClient uyumu: Varsayılan header'lar
  getDefaultHeaders() {
    return { ...this.REQUEST_CONFIG.HEADERS };
  }

  // HttpClient uyumu: Özellik bayrakları
  getFeatureFlags() {
    return {
      enableRetry: this.isProduction,
      enableRateLimit: this.isProduction,
      enableRequestLogging: this.isDevelopment,
      enableResponseCache: this.isProduction,
      enableErrorReporting: this.isProduction,
      enableRequestInterception: true,
    };
  }

  // HttpClient uyumu: URL normalize
  normalizeURL(url) {
    if (!url) throw new Error('URL zorunludur');
    if (/^https?:\/\//.test(url)) return url;
    const clean = url.startsWith('/') ? url.slice(1) : url;
    return `${this.BASE_URL}/${clean}`.replace(/([^:]\/)\/+/g, '$1');
  }

  // HttpClient uyumu: İstek doğrulama
  validateRequest(url, method, headers, body) {
    const errors = [];
    if (!url || typeof url !== 'string') errors.push('URL zorunludur');
    const validMethods = [
      'GET',
      'POST',
      'PUT',
      'PATCH',
      'DELETE',
      'HEAD',
      'OPTIONS',
    ];
    if (!validMethods.includes(String(method).toUpperCase()))
      errors.push(`Geçersiz metot: ${method}`);
    if (headers && typeof headers !== 'object')
      errors.push('Başlıklar nesne olmalıdır');
    if (
      ['POST', 'PUT', 'PATCH'].includes(String(method).toUpperCase()) &&
      body !== null
    ) {
      if (typeof body === 'object') {
        try {
          JSON.stringify(body);
        } catch {
          errors.push('İstek gövdesi döngüsel referans içeriyor');
        }
      }
    }
    return { isValid: errors.length === 0, errors };
  }

  // HttpClient uyumu: Oran sınırlama (client tarafı)
  isRateLimited(url) {
    const now = Date.now();
    const key = `rate_${url}`;
    const last = this.rateLimitMap.get(key) || 0;
    if (now - last < this.REQUEST_THROTTLE_MS) return true;
    this.rateLimitMap.set(key, now);
    return false;
  }

  // HttpClient uyumu: Ağ hatası sınıflandırma
  handleNetworkError(error, context = {}) {
    const { url, method, attempt = 1 } = context;
    let type = 'UNKNOWN_ERROR';
    let message = 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
    let shouldRetry = false;
    if (
      error?.name === 'TypeError' &&
      String(error.message).includes('fetch')
    ) {
      type = 'NETWORK_ERROR';
      message = 'İnternet bağlantınızı kontrol edin.';
      shouldRetry = true;
    } else if (error?.name === 'AbortError') {
      type = 'TIMEOUT_ERROR';
      message = 'İstek zaman aşımına uğradı.';
      shouldRetry = true;
    } else if (
      String(error.message).includes('CORS') ||
      String(error.message).includes('cross-origin')
    ) {
      type = 'CORS_ERROR';
      message = 'CORS sebebiyle istek engellendi.';
      shouldRetry = false;
    } else if (String(error.message).includes('Failed to fetch')) {
      type = 'DNS_ERROR';
      message = 'Sunucuya ulaşılamıyor.';
      shouldRetry = true;
    } else if (error?.status) {
      if (error.status >= 500) {
        type = 'SERVER_ERROR';
        message = 'Sunucu hatası.';
        shouldRetry = true;
      } else if (error.status === 404) {
        type = 'NOT_FOUND';
        message = 'Kaynak bulunamadı.';
      } else if (error.status === 401) {
        type = 'UNAUTHORIZED';
        message = 'Oturum süreniz dolmuş olabilir.';
      } else if (error.status === 403) {
        type = 'FORBIDDEN';
        message = 'Bu işlem için yetkiniz yok.';
      } else if (error.status >= 400) {
        type = 'CLIENT_ERROR';
        message = 'İstek hatası.';
      }
    }
    return { type, message, originalError: error, shouldRetry, context };
  }

  // HttpClient uyumu: ortam bilgisi
  getEnvironmentInfo() {
    return {
      isDevelopment: this.isDevelopment,
      isProduction: this.isProduction,
      baseURL: this.BASE_URL,
      currentOrigin:
        typeof window !== 'undefined' ? window.location.origin : 'N/A',
      currentHost:
        typeof window !== 'undefined' ? window.location.hostname : 'N/A',
      timestamp: new Date().toISOString(),
    };
  }

  /* ----------------------------------------------- 7) Kısa yol getter'ları */
  get auth() {
    return {
      login: this.getURL(this.ENDPOINTS.AUTH.LOGIN),
      logout: this.getURL(this.ENDPOINTS.AUTH.LOGOUT),
      refresh: this.getURL(this.ENDPOINTS.AUTH.REFRESH),
      verify: this.getURL(this.ENDPOINTS.AUTH.VERIFY),
    };
  }

  get user() {
    return {
      profile: this.getURL(this.ENDPOINTS.USER.PROFILE),
      update: this.getURL(this.ENDPOINTS.USER.UPDATE),
    };
  }

  get data() {
    return {
      list: this.getURL(this.ENDPOINTS.DATA.LIST),
      create: this.getURL(this.ENDPOINTS.DATA.CREATE),
      update: this.getURL(this.ENDPOINTS.DATA.UPDATE),
      delete: this.getURL(this.ENDPOINTS.DATA.DELETE),
    };
  }

  /* ----------------------------------------------- 8) Konsola öz bilgiler */
  #log() {
    if (this.DEVELOPMENT_MODE) {
      console.info('%c[APIConfig] Loaded', 'color:#0af');
      console.table({
        BASE_URL: this.BASE_URL,
        FRONTEND_URL: this.FRONTEND_URL,
        MODE: this.DEVELOPMENT_MODE ? 'development' : 'production',
        TIMEOUT_MS: this.REQUEST_CONFIG.TIMEOUT,
      });
    }
  }
}

/* ---------------------------------------------------- 9) Tekil örnek & export */
const apiConfig = new APIConfig();
export default apiConfig;

/* Kolay import isteyenler için named exports */
export const { BASE_URL, FRONTEND_URL, ENDPOINTS, REQUEST_CONFIG } = apiConfig;
export const urls = {
  auth: apiConfig.auth,
  user: apiConfig.user,
  data: apiConfig.data,
};

/* --------------------------------------------------------------------------
 * Kullanım:
 *   import apiConfig from '@/config/config';
 *   const res = await apiConfig.fetch('/users');         // otomatik http://78.186.223.18:9072/api/users
 * -------------------------------------------------------------------------- */
